import {
  ArrowLeft,
  Copy,
  Check,
  Server,
  Shield,
  Globe,
  Cloud,
  DollarSign,
  Bell,
  Moon,
} from "lucide-react";
import { useState, useEffect } from "react";

import { useMoriartyConfig, useUpdateMoriartyConfig } from "@/hooks/useMoriarty";
import { useStore } from "@/lib/store";

import { SidebarToggleButton } from "../SidebarToggleButton";

const PRICING_TEMPLATE = JSON.stringify(
  {
    "m7i.xlarge": { vcpu: 4, memGb: 16, hourlyUsd: 0.2016 },
  },
  null,
  2,
);

interface FieldDef {
  key: string;
  label: string;
  placeholder: string;
  description?: string;
}

interface SectionDef {
  title: string;
  icon: React.JSX.Element;
  description: string;
  fields: FieldDef[];
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TAB_INFRA_SECTIONS: SectionDef[] = [
  {
    title: "AWS",
    icon: <Server size={14} />,
    description: "EC2 instance and network configuration",
    fields: [
      {
        key: "ami_id",
        label: "AMI ID",
        placeholder: "ami-0123456789abcdef0",
        description: "Base Amazon Machine Image for Moriarty instances",
      },
      {
        key: "security_group_id",
        label: "Security Group",
        placeholder: "sg-0123456789abcdef0",
        description: "VPC security group for instance networking",
      },
      {
        key: "default_instance_type",
        label: "Default Instance Type",
        placeholder: "m7a.xlarge",
        description: "EC2 instance type used when none is specified",
      },
      {
        key: "max_concurrent_environments",
        label: "Max Concurrent",
        placeholder: "5",
        description: "Maximum number of environments running at once",
      },
      {
        key: "default_branch",
        label: "Default Branch",
        placeholder: "staging",
        description: "Git branch checked out by default",
      },
    ],
  },
  {
    title: "Tailscale",
    icon: <Shield size={14} />,
    description: "VPN mesh networking for secure access",
    fields: [
      {
        key: "tailscale_auth_key",
        label: "Auth Key",
        placeholder: "tskey-auth-...",
        description: "Tailscale authentication key for new nodes",
      },
      {
        key: "tailscale_api_key",
        label: "API Key",
        placeholder: "tskey-api-...",
        description: "Tailscale API key for managing devices",
      },
      {
        key: "tailscale_tailnet",
        label: "Tailnet",
        placeholder: "your-org.tail1234.ts.net",
        description: "Your Tailscale network domain",
      },
    ],
  },
  {
    title: "Cloudflare",
    icon: <Cloud size={14} />,
    description: "DNS management for boldorg.app subdomains",
    fields: [
      {
        key: "cloudflare_zone_id",
        label: "Zone ID",
        placeholder: "a1b2c3d4e5f6...",
        description: "Zone ID for boldorg.app (Cloudflare dashboard → Overview → API sidebar)",
      },
      {
        key: "cloudflare_api_token",
        label: "API Token",
        placeholder: "Bearer token",
        description: "Scoped to Zone:DNS:Edit on boldorg.app",
      },
    ],
  },
];

const TAB_API_SECTIONS: SectionDef[] = [
  {
    title: "API",
    icon: <Globe size={14} />,
    description: "Callback and authentication settings",
    fields: [
      {
        key: "moriarty_api_url",
        label: "Moriarty API URL",
        placeholder: "https://sherlog.bold.org",
        description: "Base URL for Moriarty API callbacks",
      },
      {
        key: "callback_secret",
        label: "Callback Secret",
        placeholder: "secret token",
        description: "Shared secret for authenticating callbacks",
      },
      {
        key: "opencode_server_password",
        label: "Opencode Server Password",
        placeholder: "password",
        description: "Password for the opencode server",
      },
    ],
  },
  {
    title: "Alerts",
    icon: <Bell size={14} />,
    description: "Slack notifications for accumulated compute spend",
    fields: [
      {
        key: "alert_channel",
        label: "Slack Channel ID",
        placeholder: "C018E60LPFX",
        description: "Channel to post alerts to (default: #alerts)",
      },
      {
        key: "alert_threshold_usd",
        label: "Cost milestone (USD)",
        placeholder: "25",
        description: "Alert at each accumulated compute-cost milestone (default: $25)",
      },
    ],
  },
  {
    title: "Auto Sleep",
    icon: <Moon size={14} />,
    description: "Idle detection, observation mode, and stopped-instance retention",
    fields: [
      {
        key: "auto_sleep_idle_minutes",
        label: "Idle timeout (minutes)",
        placeholder: "300",
        description: "Stop an environment after this much inactivity",
      },
      {
        key: "auto_sleep_warning_minutes",
        label: "Warning period (minutes)",
        placeholder: "30",
        description: "Show an idle warning this long before sleep",
      },
      {
        key: "auto_sleep_heartbeat_stale_minutes",
        label: "Heartbeat stale after (minutes)",
        placeholder: "3",
        description: "Fail open when the machine reporter has not checked in recently",
      },
      {
        key: "auto_sleep_enforced",
        label: "Enforce auto sleep",
        placeholder: "false",
        description: "Use false for observation-only warnings; true enables EC2 stop",
      },
      {
        key: "stopped_retention_days",
        label: "Stopped retention (days)",
        placeholder: "7",
        description: "Terminate stopped environments after this many days",
      },
    ],
  },
];

type TabId = "infra" | "api";

const TABS: { id: TabId; label: string; icon: React.JSX.Element }[] = [
  { id: "infra", label: "Infrastructure", icon: <Server size={12} /> },
  { id: "api", label: "API & Alerts", icon: <Globe size={12} /> },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MoriartyConfig(): React.JSX.Element {
  const setView = useStore((state) => state.setView);
  const showToast = useStore((state) => state.showToast);
  const { data, isLoading } = useMoriartyConfig();
  const updateMutation = useUpdateMoriartyConfig();

  const [activeTab, setActiveTab] = useState<TabId>("infra");
  const [values, setValues] = useState<Record<string, string>>({});
  const [pricingJson, setPricingJson] = useState("");
  const [pricingError, setPricingError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.config) {
      setValues(data.config);
      if (data.config.instance_pricing) {
        try {
          setPricingJson(JSON.stringify(JSON.parse(data.config.instance_pricing), null, 2));
        } catch {
          setPricingJson(data.config.instance_pricing);
        }
      }
    }
  }, [data?.config]);

  function handleChange(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function handlePricingChange(value: string) {
    setPricingJson(value);
    if (!value.trim()) {
      setPricingError(null);
      return;
    }
    try {
      JSON.parse(value);
      setPricingError(null);
    } catch {
      setPricingError("Invalid JSON");
    }
  }

  function handleBack() {
    setView("moriarty");
    const url = new URL(window.location.href);
    url.searchParams.set("view", "moriarty");
    window.history.pushState({}, "", url);
  }

  async function handleSave() {
    if (updateMutation.isPending || pricingError) return;
    try {
      const payload = { ...values };
      if (pricingJson.trim()) {
        payload.instance_pricing = pricingJson.trim();
      }
      await updateMutation.mutateAsync(payload);
      showToast("Config updated", "success");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update config";
      showToast(msg, "error");
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="border-cyber-border flex-shrink-0 border-b py-2 pr-2 pl-0 md:px-4 md:py-3">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-3">
            <SidebarToggleButton />
            <button
              onClick={handleBack}
              className="text-cyber-muted hover:bg-cyber-magenta/10 hover:text-cyber-magenta rounded p-1.5 transition-all"
              title="Back to Moriarty"
            >
              <ArrowLeft size={14} />
            </button>
            <h2 className="text-cyber-text text-sm font-medium">Lair Configuration</h2>
          </div>
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending || isLoading || !!pricingError}
            className="border-cyber-magenta/30 text-cyber-magenta hover:border-cyber-magenta/50 hover:bg-cyber-magenta/10 rounded border px-4 py-1.5 text-xs transition-all disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateMutation.isPending ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-cyber-border flex-shrink-0 border-b px-10">
        <div className="flex gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-xs transition-all ${
                activeTab === tab.id
                  ? "border-cyber-magenta text-cyber-magenta"
                  : "text-cyber-muted hover:text-cyber-text border-transparent"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-cyber-muted py-12 text-center">
            <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
            Loading configuration...
          </div>
        ) : (
          <div className="mx-auto max-w-4xl pb-8">
            {/* Infrastructure tab */}
            {activeTab === "infra" && (
              <div className="divide-cyber-border/50 divide-y">
                {TAB_INFRA_SECTIONS.map((section) => (
                  <SectionFields
                    key={section.title}
                    section={section}
                    values={values}
                    onChange={handleChange}
                  />
                ))}

                {/* Pricing (custom section within infra) */}
                <section className="grid grid-cols-[220px_1fr] gap-10 py-8">
                  <div className="pt-1">
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="text-cyber-magenta">
                        <DollarSign size={14} />
                      </span>
                      <h3 className="text-cyber-text text-xs font-medium tracking-wider uppercase">
                        Pricing
                      </h3>
                    </div>
                    <p className="text-cyber-muted/60 text-[11px] leading-relaxed">
                      Instance type pricing for cost estimation. Leave empty to use defaults.
                    </p>
                  </div>

                  <div>
                    <textarea
                      value={pricingJson}
                      onChange={(e) => handlePricingChange(e.target.value)}
                      placeholder='{"t3a.large": {"vcpu": 2, "memGb": 8, "hourlyUsd": 0.0752}}'
                      rows={10}
                      className={`bg-cyber-bg text-cyber-text w-full resize-y rounded border px-3 py-2 font-mono text-xs focus:outline-none ${
                        pricingError
                          ? "border-red-500/50 focus:border-red-500"
                          : "border-cyber-border focus:border-cyber-magenta/50"
                      }`}
                    />
                    {pricingError && (
                      <div className="mt-0.5 text-[10px] text-red-400">{pricingError}</div>
                    )}
                    <PricingTemplate />
                  </div>
                </section>
              </div>
            )}

            {/* API & Alerts tab */}
            {activeTab === "api" && (
              <div className="divide-cyber-border/50 divide-y">
                {TAB_API_SECTIONS.map((section) => (
                  <SectionFields
                    key={section.title}
                    section={section}
                    values={values}
                    onChange={handleChange}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared section renderer
// ---------------------------------------------------------------------------

function SectionFields({
  section,
  values,
  onChange,
}: {
  section: SectionDef;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  return (
    <section className="grid grid-cols-[220px_1fr] gap-10 py-8 first:pt-0">
      <div className="pt-1">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-cyber-magenta">{section.icon}</span>
          <h3 className="text-cyber-text text-xs font-medium tracking-wider uppercase">
            {section.title}
          </h3>
        </div>
        <p className="text-cyber-muted/60 text-[11px] leading-relaxed">{section.description}</p>
      </div>

      <div className="space-y-4">
        {section.fields.map((field) => (
          <div key={field.key} className="grid grid-cols-[140px_1fr] items-start gap-3">
            <label className="text-cyber-muted pt-1.5 text-right text-xs">{field.label}</label>
            <div>
              <input
                type="text"
                value={values[field.key] ?? ""}
                onChange={(e) => onChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-magenta/50 w-full rounded border px-3 py-1.5 font-mono text-xs focus:outline-none"
              />
              {field.description && (
                <p className="text-cyber-muted/50 mt-0.5 text-[10px]">{field.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pricing template helper
// ---------------------------------------------------------------------------

function PricingTemplate() {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(PRICING_TEMPLATE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-cyber-magenta/60 hover:text-cyber-magenta text-[10px] transition-colors"
      >
        {expanded ? "Hide" : "Show"} template
      </button>
      {expanded && (
        <div className="relative mt-1">
          <pre className="border-cyber-border bg-cyber-bg text-cyber-muted max-h-48 overflow-x-auto rounded border px-3 py-2 font-mono text-[10px]">
            {PRICING_TEMPLATE}
          </pre>
          <button
            type="button"
            onClick={handleCopy}
            className="text-cyber-muted hover:bg-cyber-magenta/10 hover:text-cyber-magenta absolute top-1.5 right-1.5 rounded p-1 transition-all"
            title="Copy template"
          >
            {copied ? <Check size={12} className="text-cyber-green" /> : <Copy size={12} />}
          </button>
        </div>
      )}
    </div>
  );
}
